import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { SiteRouteParams } from '../core/routeparams';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Site, SiteApiService } from '../core/site';

@Injectable()
export class SiteIdLoggedInCanActivateChild {
  constructor(
    private siteService: SiteApiService,
    private router: Router,
  ) {}

  public static buildLoginUrl(siteId: string, nextUrl: string): string {
    let loginUrl = `/vdc-session/transfer/site/${siteId}`;
    if (nextUrl) {
      loginUrl = loginUrl.concat(`?nextUrl=${encodeURIComponent(nextUrl)}`);
    }
    return loginUrl;
  }

  private static isTemplatePath(url: string): boolean {
    return url.indexOf('template') !== -1;
  }

  private static isSitePath(url: string): boolean {
    return url.indexOf('site') !== -1;
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const siteId = (route.params as SiteRouteParams).siteId;
    const nextUrlParam = 'nextUrl';
    let nextUrl = route.queryParams[nextUrlParam];
    if (nextUrl === '' || nextUrl === undefined || nextUrl === null) {
      nextUrl = state.url;
    }

    return this.siteService.getV2(siteId).pipe(
      switchMap((site: Site) => {
        if (!site.template) {
          // Check for migration
          return this.siteService.apiCheckMigration(siteId).pipe(
            map((res) => {
              if (res && res.redirect_url && res.redirect_url !== '') {
                return this.router.parseUrl(res.redirect_url);
              }
              return null;
            }),
            switchMap((redirectUrl) => {
              if (redirectUrl) {
                return of(redirectUrl);
              }
              // Continue with the original logic if no redirect URL is provided
              return of(site);
            }),
          );
        }

        // Continue with the original logic if the site has a template
        return of(site);
      }),
      map((siteOrRedirect: Site | UrlTree) => {
        if (siteOrRedirect instanceof UrlTree) {
          return siteOrRedirect;
        }

        const site = siteOrRedirect as Site;
        if (SiteIdLoggedInCanActivateChild.isTemplatePath(nextUrl)) {
          return site.template ? true : this.router.parseUrl(`/site/${siteId}/overview`);
        }
        if (SiteIdLoggedInCanActivateChild.isSitePath(nextUrl)) {
          return site.template ? this.router.parseUrl(`/template/${siteId}/overview`) : true;
        }
        return true;
      }),
      catchError(() => {
        window.location.href = SiteIdLoggedInCanActivateChild.buildLoginUrl(siteId, nextUrl);
        return of(null);
      }),
    );
  }
}
